/**
 * billing.ts - Billing dashboard and subscription management
 */

import { API_URL } from '../utils/constants';
import { getCurrentUser } from '../auth/auth-state';

export interface SubscriptionStatus {
  tier: 'free' | 'pro';
  status: 'active' | 'inactive' | 'cancelled' | 'past_due';
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelledAt?: string;
}

export interface BillingHistoryItem {
  id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  invoiceDate: string;
}

/**
 * Show billing modal
 */
export function showBillingModal(): void {
  const modal = document.getElementById('billing-modal');
  if (!modal) return;

  modal.style.display = 'block';

  // Check if user is actually logged in before loading
  const user = getCurrentUser();
  if (!user) {
    const statusContainer = document.getElementById('billing-status-container');
    if (statusContainer) {
      const i18n = window.i18n;
      const loginMessage = i18n ? i18n.t('billing.pleaseLogin') : 'Please log in to view your billing information.';
      const loginBtn = i18n ? i18n.t('header.login') : 'Login';
      statusContainer.innerHTML = `
        <div class="billing-login-prompt">
          <p>${loginMessage}</p>
          <button type="button" class="btn btn-primary" onclick="closeBillingModal(); openAuthModal('login');">${loginBtn}</button>
        </div>
      `;
    }
    return;
  }

  loadBillingData();
}

/**
 * Close billing modal
 */
export function closeBillingModal(): void {
  const modal = document.getElementById('billing-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Load billing data
 */
async function loadBillingData(): Promise<void> {
  const statusContainer = document.getElementById('billing-status-container');
  const historyContainer = document.getElementById('billing-history-container');
  const i18n = window.i18n;

  if (!statusContainer) return;

  try {
    // Load subscription status
    const statusResponse = await fetch(`${API_URL}/subscriptions/status`, {
      credentials: 'include',
    });

    if (statusResponse.status === 401) {
      // User is not authenticated - show login prompt
      const loginMessage = i18n ? i18n.t('billing.pleaseLogin') : 'Please log in to view your billing information.';
      const loginBtn = i18n ? i18n.t('header.login') : 'Login';
      statusContainer.innerHTML = `
        <div class="billing-login-prompt">
          <p>${loginMessage}</p>
          <button type="button" class="btn btn-primary" onclick="closeBillingModal(); openAuthModal('login');">${loginBtn}</button>
        </div>
      `;
      return;
    }

    if (!statusResponse.ok) {
      throw new Error('Failed to load subscription status');
    }

    const status: SubscriptionStatus = await statusResponse.json();

    // Render subscription status
    renderSubscriptionStatus(status, statusContainer);

    // Load billing history
    if (historyContainer) {
      const historyResponse = await fetch(`${API_URL}/subscriptions/history`, {
        credentials: 'include',
      });

      if (historyResponse.ok) {
        const history: BillingHistoryItem[] = await historyResponse.json();
        renderBillingHistory(history, historyContainer);
      }
    }
  } catch (error) {
    console.error('Billing data error:', error);
    if (statusContainer) {
      const errorMessage = i18n ? i18n.t('billing.loadError') : 'Failed to load billing information';
      statusContainer.innerHTML = `<p class="error">${errorMessage}</p>`;
    }
  }
}

/**
 * Render subscription status
 */
function renderSubscriptionStatus(status: SubscriptionStatus, container: HTMLElement): void {
  const i18n = window.i18n;
  const tierLabel = status.tier === 'pro' ? (i18n ? i18n.t('billing.proPlan') : 'Pro Plan') : (i18n ? i18n.t('billing.freePlan') : 'Free Plan');

  let html = `
    <div class="billing-current-plan">
      <h3>${i18n ? i18n.t('billing.currentPlan') : 'Current Plan'}</h3>
      <div class="plan-details">
        <p><strong>${i18n ? i18n.t('billing.tier') : 'Tier'}:</strong> ${tierLabel}</p>
  `;

  // Only show status for pro tier
  if (status.tier === 'pro') {
    const statusLabel = getStatusLabel(status.status);
    html += `<p><strong>${i18n ? i18n.t('billing.status') : 'Status'}:</strong> <span class="status-badge status-${status.status}">${statusLabel}</span></p>`;
  } else {
    // For free tier, show "No active subscription"
    html += `<p><strong>${i18n ? i18n.t('billing.status') : 'Status'}:</strong> ${i18n ? i18n.t('billing.noActiveSubscription') : 'No active subscription'}</p>`;
  }

  html += `
  `;

  if (status.currentPeriodEnd) {
    const renewDate = new Date(status.currentPeriodEnd).toLocaleDateString();
    html += `<p><strong>${i18n ? i18n.t('billing.renewsOn') : 'Renews on'}:</strong> ${renewDate}</p>`;
  }

  if (status.cancelledAt) {
    const cancelDate = new Date(status.cancelledAt).toLocaleDateString();
    html += `<p class="warning"><strong>${i18n ? i18n.t('billing.cancelledOn') : 'Cancelled on'}:</strong> ${cancelDate}</p>`;
  }

  html += `</div></div>`;

  // Add action buttons
  html += '<div class="billing-actions">';

  if (status.tier === 'free') {
    html += `<button class="btn btn-primary" onclick="showPricingModal(); closeBillingModal();">${i18n ? i18n.t('billing.upgradeToPro') : 'Upgrade to Pro'}</button>`;
  } else {
    html += `<button class="btn btn-secondary" onclick="manageBillingPortal()">${i18n ? i18n.t('billing.manageSubscription') : 'Manage Subscription'}</button>`;
    if (status.status === 'active' && !status.cancelledAt) {
      html += `<button class="btn btn-danger" onclick="cancelSubscription()">${i18n ? i18n.t('billing.cancelSubscription') : 'Cancel Subscription'}</button>`;
    }
  }

  html += '</div>';

  container.innerHTML = html;
}

/**
 * Get status label
 */
function getStatusLabel(status: string): string {
  const i18n = window.i18n;
  const labels: Record<string, string> = {
    active: i18n ? i18n.t('billing.statusActive') : 'Active',
    inactive: i18n ? i18n.t('billing.statusInactive') : 'Inactive',
    cancelled: i18n ? i18n.t('billing.statusCancelled') : 'Cancelled',
    past_due: i18n ? i18n.t('billing.statusPastDue') : 'Past Due',
  };
  return labels[status] || status;
}

/**
 * Render billing history
 */
function renderBillingHistory(history: BillingHistoryItem[], container: HTMLElement): void {
  const i18n = window.i18n;

  if (history.length === 0) {
    container.innerHTML = `<p>${i18n ? i18n.t('billing.noBillingHistory') : 'No billing history'}</p>`;
    return;
  }

  let html = `
    <h3>${i18n ? i18n.t('billing.billingHistory') : 'Billing History'}</h3>
    <table class="billing-history-table">
      <thead>
        <tr>
          <th>${i18n ? i18n.t('billing.date') : 'Date'}</th>
          <th>${i18n ? i18n.t('billing.amount') : 'Amount'}</th>
          <th>${i18n ? i18n.t('billing.status') : 'Status'}</th>
        </tr>
      </thead>
      <tbody>
  `;

  history.forEach(item => {
    const date = new Date(item.invoiceDate).toLocaleDateString();
    const amount = (item.amount / 100).toFixed(2);
    const statusLabel = getStatusLabel(item.status);

    html += `
      <tr>
        <td>${date}</td>
        <td>$${amount} ${item.currency}</td>
        <td><span class="status-badge status-${item.status}">${statusLabel}</span></td>
      </tr>
    `;
  });

  html += '</tbody></table>';
  container.innerHTML = html;
}

/**
 * Open Stripe billing portal
 */
export async function manageBillingPortal(): Promise<void> {
  try {
    const response = await fetch(`${API_URL}/subscriptions/portal`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to create billing portal session');
    }

    const data = await response.json();

    if (data.url) {
      window.location.href = data.url;
    } else {
      throw new Error('No portal URL received');
    }
  } catch (error) {
    console.error('Billing portal error:', error);
    const errorPrefix = window.i18n ? window.i18n.t('billing.portalError') : 'Failed to open billing portal';
    alert(`${errorPrefix}: ${(error as Error).message}`);
  }
}

/**
 * Cancel subscription
 */
export async function cancelSubscription(): Promise<void> {
  try {
    const response = await fetch(`${API_URL}/subscriptions/cancel`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to cancel subscription');
    }

    // Show brief confirmation before reloading
    const cancelBtn = document.querySelector('button[onclick="cancelSubscription()"]') as HTMLButtonElement;
    if (cancelBtn) {
      cancelBtn.textContent = '✓ Cancelled';
      cancelBtn.style.backgroundColor = '#28a745';
      cancelBtn.disabled = true;
    }

    // Reload billing data after brief delay
    setTimeout(() => loadBillingData(), 1500);
  } catch (error) {
    console.error('Cancel subscription error:', error);
    const errorPrefix = window.i18n ? window.i18n.t('billing.cancelError') : 'Failed to cancel subscription';
    alert(`${errorPrefix}: ${(error as Error).message}`);
  }
}

// Make functions globally available
(window as any).showBillingModal = showBillingModal;
(window as any).closeBillingModal = closeBillingModal;
(window as any).manageBillingPortal = manageBillingPortal;
(window as any).cancelSubscription = cancelSubscription;

// Auto-open billing modal if on /billing route
if (typeof window !== 'undefined') {
  // Check on initial load
  if (window.location.pathname === '/billing') {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        showBillingModal();
      });
    } else {
      showBillingModal();
    }
  }
}
